import { useSendPasswordResetEmail } from "react-firebase-hooks/auth";
import { auth } from "../config/firebaseConfig";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import AuthShell from "@/components/auth/AuthShell";

const ForgotPasswordView = () => {
  const { t } = useTranslation(["auth", "common"]);
  const [sendPasswordResetEmail, sending, error] =
    useSendPasswordResetEmail(auth);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);

  const onFinish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || sending) return;

    // Firebase rejects action code settings without a url, so an unset
    // VITE_APP_URL must mean "no continue url" rather than "url: undefined".
    const url = import.meta.env.VITE_APP_URL;
    const actionCodeSettings = url ? { url } : undefined;

    setFailed(false);
    try {
      const res = await sendPasswordResetEmail(email, actionCodeSettings);

      if (res) {
        setSent(true);
        toast({
          title: t("auth:forgotPassword.emailSent"),
        });
        setEmail("");
      } else {
        setFailed(true);
        toast({
          title: t("common:somethingWentWrong"),
          variant: "destructive",
        });
      }
    } catch {
      setFailed(true);
      toast({
        title: t("common:somethingWentWrong"),
        variant: "destructive",
      });
    }
  };

  return (
    <AuthShell
      title={t("auth:forgotPassword.title")}
      subtitle={t("auth:forgotPassword.subtitle")}
    >
      <div className="space-y-6">
        {(failed || error) && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm"
          >
            <div className="flex items-center gap-2 font-semibold text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {t("auth:forgotPassword.failedTitle")}
            </div>
            {/* Never render error.message here - Firebase's raw
                "Firebase: Error (auth/user-not-found)." both leaks whether an
                account exists and is meaningless to the user. */}
            <p className="mt-2 text-destructive/90">
              {t("auth:forgotPassword.failedDescription")}
            </p>
          </div>
        )}

        {sent && !failed && !error && (
          <div
            role="status"
            className="rounded-lg border border-border bg-muted/40 p-4 text-sm"
          >
            <div className="flex items-center gap-2 font-semibold text-foreground">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
              {t("auth:forgotPassword.sentTitle")}
            </div>
            <p className="mt-2 text-muted-foreground">
              {t("auth:forgotPassword.sentDescription")}
            </p>
          </div>
        )}

        <form onSubmit={onFinish} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email">{t("auth:fields.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth:fields.emailPlaceholder")}
              autoComplete="username"
              className="h-11"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={sending}
            className="h-11 w-full bg-foreground text-background hover:bg-foreground/90"
          >
            {sending ? t("common:sending") : t("auth:forgotPassword.submit")}
          </Button>

          <div className="text-center">
            <Link
              to="/sign-in"
              className="text-sm text-primary hover:underline"
            >
              {t("auth:forgotPassword.backToSignIn")}
            </Link>
          </div>
        </form>
      </div>
    </AuthShell>
  );
};

export default ForgotPasswordView;
